import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	Signal,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { finalize, Subject, takeUntil } from 'rxjs'
import { LanguageEnum } from '@modules/lang-module/enums/language.enum'
import { LanguageService } from '@modules/lang-module/services/language.service'
import { ThemeEnum } from '@modules/theme-module/enums/theme.enum'
import { ThemeService } from '@modules/theme-module/services/theme.service'
import { LiveTickerInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker.interface'
import { TwelveDataService } from '@modules/twelve-data-module/services/twelve-data.service'
import { PriceTargetInterface } from '@modules/twelve-data-module/interfaces/price-target.interface'
import { RecommendationsInterface } from '@modules/twelve-data-module/interfaces/recommendations.interface'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { PriceForecastComponent } from './common/price-forecast/price-forecast.component'
import { TechAnalysisComponent } from './common/tech-analysis/tech-analysis.component'
import { AnalystsForecastComponent } from './common/analysts-forecast/analysts-forecast.component'
import { RecommendationsMonthInterface } from '@modules/twelve-data-module/interfaces/recommendations-month.interface'

@Component({
	selector: 'app-info-stocks-analytics',
	templateUrl: './info-stocks-analytics.component.html',
	styleUrl: './info-stocks-analytics.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [PriceForecastComponent, TechAnalysisComponent, AnalystsForecastComponent],
})
export class InfoStocksAnalyticsComponent implements OnInit, OnDestroy, OnChanges {
	protected readonly languageEnum: typeof LanguageEnum = LanguageEnum
	protected readonly themeEnum: typeof ThemeEnum = ThemeEnum

	public widget: InputSignal<WidgetInterface | null> = input<WidgetInterface | null>(null)
	public liveTicker: InputSignal<LiveTickerInterface | null> = input<LiveTickerInterface | null>(null)

	private readonly _languageService: LanguageService = inject<LanguageService>(LanguageService)
	private readonly _themeService: ThemeService = inject<ThemeService>(ThemeService)
	private readonly _twelveDataService: TwelveDataService = inject<TwelveDataService>(TwelveDataService)

	private destroy$: Subject<void> = new Subject<void>()

	protected lang: WritableSignal<LanguageEnum> = signal<LanguageEnum>(this.languageEnum.ENGLISH)
	protected theme: WritableSignal<ThemeEnum> = signal<ThemeEnum>(this.themeEnum.LIGHT)

	protected priceTarget: WritableSignal<PriceTargetInterface | null> = signal<PriceTargetInterface | null>(null)
	protected isLoadingPriceTarget: WritableSignal<boolean> = signal<boolean>(false)

	protected recommendations: WritableSignal<RecommendationsInterface | null> =
		signal<RecommendationsInterface | null>(null)
	protected isLoadingRecommendations: WritableSignal<boolean> = signal<boolean>(false)

	protected totalAnalysts: Signal<number> = computed<number>((): number => {
		const recommendations: RecommendationsInterface | null = this.recommendations()
		if (!recommendations?.trends?.current_month) return 0
		const {
			strong_buy = 0,
			buy = 0,
			hold = 0,
			sell = 0,
			strong_sell = 0,
		}: RecommendationsMonthInterface = recommendations.trends.current_month
		return strong_buy + buy + hold + sell + strong_sell
	})
	private _listenLanguage(): void {
		this._languageService.language$.pipe(takeUntil(this.destroy$)).subscribe((response: LanguageEnum): void => {
			this.lang.set(response)
		})
	}

	private _listenTheme(): void {
		this._themeService.theme$.pipe(takeUntil(this.destroy$)).subscribe((response: ThemeEnum): void => {
			this.theme.set(response)
		})
	}

	private _getPriceTarget(): void {
		const ticker: string | undefined = this.liveTicker()?.ticker
		if (!ticker) return

		this.isLoadingPriceTarget.set(true)
		this._twelveDataService
			.getPriceTarget(ticker)
			.pipe(
				takeUntil(this.destroy$),
				finalize((): void => this.isLoadingPriceTarget.set(false)),
			)
			.subscribe((response: PriceTargetInterface): void => {
				this.priceTarget.set(response)
			})
	}

	private _getRecommendations(): void {
		const ticker: string | undefined = this.liveTicker()?.ticker
		if (!ticker) return

		this.isLoadingRecommendations.set(true)
		this._twelveDataService
			.getRecommendations(ticker)
			.pipe(
				takeUntil(this.destroy$),
				finalize((): void => this.isLoadingRecommendations.set(false)),
			)
			.subscribe((response: RecommendationsInterface): void => {
				this.recommendations.set(response)
			})
	}

	ngOnInit(): void {
		this._listenLanguage()
		this._listenTheme()
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes['liveTicker']) return

		const currentTicker: string | undefined = changes['liveTicker'].currentValue?.ticker
		const previousTicker: string | undefined = changes['liveTicker'].previousValue?.ticker

		if (currentTicker !== previousTicker) {
			this._getPriceTarget()
			this._getRecommendations()
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next()
		this.destroy$.complete()
	}
}
